/**
 * Resolves marketing-site experiments from LaunchDarkly.
 *
 * Replaces the Netlify branch split, which is deprecated. Where that mechanism
 * needed a long-lived `test-production` branch and a rebuild to change
 * anything, this evaluates a flag in the browser so the split is a dashboard
 * setting.
 *
 * Deliberately does NOT report metrics to LaunchDarkly. Segment stays the
 * source of truth: the exposure event and the `test-name` / `test-variant`
 * stamping in services/Analytics.js already feed the warehouse, and reusing
 * them means no dashboard rework. See BIO-1639.
 *
 * Registered after the Segment plugin so the visitor key can come from
 * Segment's anonymousId — that shared key is what lets the data team join an
 * exposure to a sign-up.
 */
import { initialize } from 'launchdarkly-js-client-sdk';
import type { LDClient } from 'launchdarkly-js-client-sdk';
import Analytics from '@/services/Analytics';
import { ExperimentFlags } from '@/constants/Experiments';
import { setExperiment, setFlags, setReady } from '@/hooks/useExperiment';
import { getFromLocalStorage, saveInLocalStorage } from '@/utils/localStorage';

/**
 * The visitor's LaunchDarkly context key, persisted on first resolution.
 *
 * Stability is the whole game. LaunchDarkly buckets by hashing this key, so the
 * same key always lands in the same arm — but only if the key never changes.
 * Previously this preferred Segment's anonymousId and fell back to a generated
 * id per page load without persisting the former, so a visitor whose Segment
 * loaded fast on the homepage and slow on /registration got two different keys,
 * two different buckets, and flipped arms mid-funnel.
 *
 * Now whichever key wins the first race is written here and reused for every
 * subsequent load. Segment's anonymousId is still preferred on that first
 * resolution, because it is what the warehouse joins exposure to conversion on.
 */
const VISITOR_KEY_STORAGE = 'LD_ANONYMOUS_KEY';

// Segment's own load is not guaranteed. Cap the wait rather than leaving the
// homepage's experiment unresolved behind a script that may never arrive.
const SEGMENT_READY_TIMEOUT_MS = 2000;

// LaunchDarkly's own initialization cap, in seconds. The tested copy sits below
// the hero, so a visitor has to scroll before this matters — but a hung request
// must never hold the page.
const LD_INIT_TIMEOUT_S = 2;

const generateKey = (): string =>
  `anon-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

const storedKey = (): string | undefined =>
  getFromLocalStorage(VISITOR_KEY_STORAGE) || undefined;

// Persist on first resolution so every later load reuses it. Best-effort: a
// blocked storage jar costs bucketing stability, not the page.
const persistKey = (key: string): string => {
  saveInLocalStorage(VISITOR_KEY_STORAGE, key);

  return key;
};

/**
 * The context key for this visitor.
 *
 * Returns the persisted key immediately if there is one — no Segment wait, no
 * chance of a different answer than last time. Only a first-ever visit races
 * Segment for its anonymousId, falling back to a generated id if Segment is
 * slow or blocked. Whichever wins is persisted and becomes the answer forever.
 */
const getVisitorKey = (): Promise<string> => {
  const existing = storedKey();
  if (existing) {
    return Promise.resolve(existing);
  }

  return new Promise((resolve) => {
    let settled = false;

    const settle = (key: string) => {
      if (!settled) {
        settled = true;
        resolve(persistKey(key));
      }
    };

    const timer = setTimeout(
      () => settle(generateKey()),
      SEGMENT_READY_TIMEOUT_MS
    );

    try {
      window.analytics?.ready(() => {
        clearTimeout(timer);
        settle(window.analytics?.user?.()?.anonymousId?.() || generateKey());
      });
    } catch (err) {
      clearTimeout(timer);
      settle(generateKey());
    }
  });
};

const resolveFlags = (client: LDClient): Record<string, string> =>
  ExperimentFlags.reduce((acc, { key, fallback }) => {
    acc[key] = client.variation(key, fallback);

    return acc;
  }, {} as Record<string, string>);

export default async (): Promise<void> => {
  const clientId = process.env.LAUNCH_DARKLY_CLIENT_ID;

  // No client ID on local dev and on deploy previews that have not been given
  // one, or nothing under test (BIO-2166): skip LaunchDarkly entirely.
  if (!clientId || !ExperimentFlags.length) {
    setReady();

    return;
  }

  try {
    const key = await getVisitorKey();

    const client = initialize(
      clientId,
      // `user` + anonymous, which is LaunchDarkly's standard shape for
      // logged-out traffic and the only context kind our project defines today.
      // Nobody is logged in on the marketing site, so `anonymous: true` is what
      // carries that meaning.
      //
      // A dedicated `visitor` kind would be a better fit — it would keep
      // marketing traffic separate from the app's real accounts in the context
      // list and in context billing. It is a one-line change here plus adding
      // the kind in LaunchDarkly project settings. Not done because the kind
      // does not exist yet, and targeting rules written against a kind the SDK
      // never sends simply never match: the flag would serve its off-variation
      // forever while looking correctly configured.
      { kind: 'user', key, anonymous: true },
      // Serves the previous visit's values immediately, so returning visitors
      // never see control copy flash before the variant resolves.
      { bootstrap: 'localStorage', diagnosticOptOut: true }
    );

    await client.waitForInitialization(LD_INIT_TIMEOUT_S);

    const flags = resolveFlags(client);
    setFlags(flags);

    // One experiment is live at a time today, so the first configured flag is
    // the one reported to Segment. Revisit when concurrent tests land.
    const [primary] = ExperimentFlags;
    if (primary) {
      setExperiment(primary.experiment, flags[primary.key]);

      // Exposure. Analytics.track stamps test-name and test-variant from the
      // store we just populated, so the payload stays consistent with every
      // downstream conversion event.
      Analytics?.track('ab-test-entry', {});
    }
  } catch (err) {
    // A failed flag resolution must never take the homepage with it. The
    // visitor gets control copy and no exposure event, which is the correct
    // outcome — an unbucketed visitor should not appear in the results.
    console.error('LaunchDarkly initialization failed', err);
  } finally {
    setReady();
  }
};
